import { Box, Divider, Paper, Typography } from "@mui/material";
import { InfoGrid, InfoGridItem } from "../../sawlines/components/primitives";
import type { RollingTotalData } from "../models/MillOverview";

type Props = {
	data: RollingTotalData;
};

const formatNumber = (
	value: number | null | undefined,
	decimals = 0,
): string => {
	if (value === undefined || value === null) return "-";
	return Number(value.toFixed(decimals)).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimals,
	});
};

const bold = (value: string) => (
	<span style={{ fontWeight: 600 }}>{value}</span>
);

// Actual in bold, scheduled stays gray/regular — same contrast as the header.
const actualOverScheduled = (current: string, scheduled: string) => (
	<>
		{bold(current)}
		{scheduled && <span style={{ color: "#666" }}>/{scheduled}</span>}
	</>
);

export const RollingTotalStats = ({ data }: Props) => {
	const rows: InfoGridItem[] = [
		{
			label: "Billet Tons Charged",
			value: actualOverScheduled(
				formatNumber(data.BilletTonsCharged),
				formatNumber(data.BilletTonsChargedScheduled),
			),
		},
		{
			label: "Billet Pc's. Charged",
			value: actualOverScheduled(
				formatNumber(data.BilletPcsCharged),
				formatNumber(data.BilletPcsChargedScheduled),
			),
		},
		{
			label: "Rolled Tons",
			value: actualOverScheduled(
				formatNumber(data.RolledTons),
				formatNumber(data.RolledTonsScheduled),
			),
		},
		{
			label: "Finished Tons",
			value: actualOverScheduled(
				formatNumber(data.FinishedTons),
				formatNumber(data.FinishedTonsScheduled),
			),
		},
		{
			label: "Finished Bundles",
			value:
				data.FinishedBundles != null
					? bold(formatNumber(data.FinishedBundles))
					: "-",
		},
		{
			label: "Mill Yield",
			value: actualOverScheduled(
				`${formatNumber(data.MillYield, 1)}%`,
				`${formatNumber(data.MillYieldScheduled, 1)}%`,
			),
		},
		{
			label: "Finished Yield",
			value: actualOverScheduled(
				`${formatNumber(data.FinishedYield, 1)}%`,
				`${formatNumber(data.FinishedYieldScheduled, 1)}%`,
			),
		},
	];

	const statsRows: InfoGridItem[] = [
		{
			label: "Total Cobbles",
			value: formatNumber(data.TotalCobbles),
		},
		{
			label: "Production",
			value: (
				<span style={{ color: "#4CAF50" }}>
					{formatNumber(data.Production, 2)}%
				</span>
			),
		},
		{
			label: "Delays",
			value: (
				<span style={{ color: "#F44336" }}>
					{formatNumber(data.Delays, 2)}%
				</span>
			),
		},
	];

	// Actual on the left, scheduled/targeted on the right — one row per metric.
	const pairedRows: InfoGridItem[] = [
		{
			label: "Hours Actual",
			value: bold(formatNumber(data.HoursActual, 2)),
		},
		{
			label: "Scheduled",
			value: formatNumber(data.HoursScheduled, 2),
		},
		{
			label: "TPH Actual",
			value: bold(formatNumber(data.TPHActual, 2)),
		},
		{
			label: "Targeted",
			value: formatNumber(data.TPHTargeted, 2),
		},
	];

	return (
		<Paper elevation={1} sx={{ p: 1 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					mb: 0.5,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					Rolling Total
				</Typography>
				<Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
					<span style={{ fontWeight: 700, color: "#1f2937" }}>Actual</span>
					/Scheduled
				</Typography>
			</Box>

			<InfoGrid columns={1} items={rows} />
			<Divider sx={{ my: 0.75 }} />
			<InfoGrid columns={1} items={statsRows} />
			<InfoGrid columns={2} items={pairedRows} />
		</Paper>
	);
};
